import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Pencil, Trash2 } from 'lucide-react';
import type { RefereeQuestion } from '../types/referee-question.types';

interface QuestionCardProps {
  question: RefereeQuestion;
  onEdit: (question: RefereeQuestion) => void;
  onDelete: (questionId: string) => void;
}

export function QuestionCard({ question, onEdit, onDelete }: QuestionCardProps): React.JSX.Element {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = (): void => {
    onDelete(question.id);
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <Card>
        <CardContent className="flex items-center justify-between p-4">
          <div className="flex-1">
            <p className="font-medium">{question.text}</p>
            <Badge variant={question.isRequired ? 'default' : 'secondary'} className="mt-2">
              {question.isRequired ? 'Required' : 'Optional'}
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="icon" aria-label="Edit" onClick={() => onEdit(question)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Delete"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this question? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
